import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { CheckIcon, PlusIcon, SearchIcon } from 'lucide-react';
import {
    createContext,
    Fragment,
    useContext,
    useMemo,
    useRef,
    useState,
    type ComponentPropsWithRef,
    type ReactElement,
    type ReactNode,
} from 'react';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
    type DialogBodyProps,
    type DialogCloseProps,
    type DialogContentProps,
    type DialogFooterProps,
} from '../containers/dialog.tsx';
import { Button, type ButtonVariantProps } from '../primitives/button.tsx';
import { checkboxVariants } from '../primitives/checkbox.tsx';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../primitives/input-group.tsx';
import {
    MenuItemLeading,
    MenuItemTrailing,
    type MenuItemLeadingProps,
    type MenuItemTrailingProps,
} from '../primitives/menu.tsx';
import { cn } from '../util/classes.ts';
import { isGrouped, isSameOption, optionLabel, type Option, type OptionGroup } from '../util/options.ts';
import { useControllableState } from '../util/use-controllable-state.tsx';
import { EmptyState, type EmptyStateProps } from './empty-state.tsx';
import {
    PickerValue,
    PickerValueAction,
    PickerValueChip,
    PickerValueChips,
    PickerValueSuggestion,
    type PickerValueChipProps,
    type PickerValueChipsProps,
    type PickerValueProps,
    type PickerValueSuggestionProps,
} from './picker-value.tsx';

/** The default option shape: `label` is shown and searched, `value` identifies. */
export type ComboboxOption = Option;

/** A labelled section of options; `value` is both its key and its heading. */
export type ComboboxOptionGroup<Item> = OptionGroup<Item>;

export type ComboboxFilter<Item> = (
    item: Item,
    query: string,
    itemToString?: (item: Item) => string,
) => boolean;

const emptyItems: readonly never[] = [];

type ComboboxContextValue = {
    title: string;
    searchPlaceholder: string;
    multiple: boolean;
    /** Whether anything is committed — pending ticks do not count. */
    hasValue: boolean;
    isSelected: (item: unknown) => boolean;
    remove: (item: unknown) => void;
    confirm: () => void;
    open: boolean;
    setOpen: (open: boolean) => void;
    /** Whether an option was already picked when the surface opened. */
    isPinned: ((item: unknown) => boolean) | undefined;
    query: string;
};

const ComboboxContext = createContext<ComboboxContextValue | undefined>(undefined);

function useCombobox(): ComboboxContextValue {
    const context = useContext(ComboboxContext);

    if (context === undefined) {
        throw new Error('Combobox parts have to be rendered inside a <Combobox>.');
    }

    return context;
}

type ComboboxItemContextValue = {
    label: string;
    selected: boolean;
};

const ComboboxItemContext = createContext<ComboboxItemContextValue>({ label: '', selected: false });

type Boolish = boolean | undefined;

type ComboboxValue<Item, Multiple extends Boolish> = Multiple extends true ? readonly Item[] : Item;

/**
 * `Item` is inferred from `value` / `defaultValue` only: `items` takes either shape, so
 * inferring from it would pick a group as the option. Pass the type argument otherwise.
 */
export type ComboboxProps<Item, Multiple extends Boolish = false> = {
    children?: ReactNode;
    /** The surface's title, and the control's accessible name. */
    title: string;
    searchPlaceholder: string;
    items?: readonly NoInfer<Item>[] | readonly ComboboxOptionGroup<NoInfer<Item>>[];
    /** Replaces the built-in matching. `null` leaves the caller to filter `items`. */
    filter?: ComboboxFilter<Item> | null;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    onQueryChange?: (query: string) => void;
    multiple?: Multiple;
    value?: ComboboxValue<Item, Multiple>;
    defaultValue?: ComboboxValue<Item, Multiple>;
    onValueChange?: (
        value: ComboboxValue<NoInfer<Item>, Multiple> | (Multiple extends true ? never : undefined),
    ) => void;
};

type ComboboxImplProps<Item> = Omit<
    ComboboxProps<Item>,
    'items' | 'multiple' | 'value' | 'defaultValue' | 'onValueChange'
> & {
    items?: readonly Item[] | readonly ComboboxOptionGroup<Item>[];
};

// One object type each, not a union: that is what the dispatcher can narrow and spread.
type SingleImplProps<Item> = ComboboxImplProps<Item> & {
    multiple?: false;
    value?: Item;
    defaultValue?: Item;
    onValueChange?: (value: Item | undefined) => void;
};

type MultipleImplProps<Item> = ComboboxImplProps<Item> & {
    multiple: true;
    value?: readonly Item[];
    defaultValue?: readonly Item[];
    onValueChange?: (value: readonly Item[]) => void;
};

type ComboboxSurface<Item> = {
    setOpen: (open: boolean) => void;
    context: Omit<
        ComboboxContextValue,
        'multiple' | 'hasValue' | 'isSelected' | 'remove' | 'confirm' | 'isPinned'
    >;
    rootProps: {
        inline: true;
        open: boolean;
        filter: ComboboxFilter<Item> | null | undefined;
        itemToStringLabel: (item: unknown) => string;
        isItemEqualToValue: (item: unknown, other: unknown) => boolean;
        onInputValueChange: (next: string) => void;
    };
};

/** Open, query and the search field — the parts both flavours share. */
function useComboboxSurface<Item>({
    title,
    searchPlaceholder,
    filter,
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    onQueryChange,
}: Omit<ComboboxImplProps<Item>, 'items'>): ComboboxSurface<Item> {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useControllableState({
        value: openProp,
        defaultValue: defaultOpen,
        onChange: onOpenChange,
    });

    return {
        setOpen,
        context: { title, searchPlaceholder, open, setOpen, query },
        // `inline` renders the list in our Dialog instead of a Base UI popup, so dismissal
        // and focus stay with the dialog.
        rootProps: {
            inline: true as const,
            open,
            filter,
            itemToStringLabel: optionLabel,
            isItemEqualToValue: isSameOption,
            onInputValueChange: (next: string): void => {
                setQuery(next);
                onQueryChange?.(next);
            },
        },
    };
}

/** Picks one option; picking commits and closes. */
function SingleCombobox<Item = ComboboxOption>(props: SingleImplProps<Item>): ReactElement {
    const { children, items } = props;
    const { setOpen, context: surface, rootProps } = useComboboxSurface(props);

    // The empty state *is* `undefined`, so passing `value` at all is what makes it controlled.
    const [values, commit] = useControllableState<readonly Item[]>({
        value: 'value' in props ? (props.value === undefined ? emptyItems : [props.value]) : undefined,
        defaultValue: props.defaultValue === undefined ? emptyItems : [props.defaultValue],
        onChange: (next) => {
            props.onValueChange?.(next[0]);
        },
    });

    const context: ComboboxContextValue = {
        ...surface,
        multiple: false,
        hasValue: values.length > 0,
        isSelected: (item) => values.some((held) => isSameOption(held, item)),
        remove: (item) => {
            commit(values.filter((held) => !isSameOption(held, item)));
        },
        confirm: () => {
            setOpen(false);
        },
        isPinned: undefined,
    };

    return (
        <ComboboxPrimitive.Root
            {...rootProps}
            items={items}
            onOpenChange={setOpen}
            value={values[0] ?? null}
            onValueChange={(next) => {
                commit(next === null ? emptyItems : [next]);
            }}
        >
            <ComboboxContext.Provider value={context}>{children}</ComboboxContext.Provider>
        </ComboboxPrimitive.Root>
    );
}

/** Picks several options. The surface holds what is ticked until `ComboboxConfirm` commits it. */
function MultipleCombobox<Item = ComboboxOption>(props: MultipleImplProps<Item>): ReactElement {
    const { children, items } = props;
    const { setOpen, context: surface, rootProps } = useComboboxSurface(props);

    const [values, commit] = useControllableState<readonly Item[]>({
        value: 'value' in props ? (props.value ?? emptyItems) : undefined,
        defaultValue: props.defaultValue ?? emptyItems,
        onChange: (next) => {
            props.onValueChange?.(next);
        },
    });

    const [pendingValues, setPendingValues] = useState<readonly Item[]>(values);
    // Snapshot the committed values, so confirming does not reorder the rows behind the
    // dialog while it is still fading out.
    const [pinnedValues, setPinnedValues] = useState<readonly Item[]>(values);
    const [wasOpen, setWasOpen] = useState(surface.open);

    // Seed on the transition, not in a handler: `open` can be controlled from outside.
    if (surface.open !== wasOpen) {
        setWasOpen(surface.open);

        if (surface.open) {
            setPendingValues(values);
            setPinnedValues(values);
        }
    }

    // Grouped `items` are the caller's own sections, so the pinned split stands aside.
    const flatItems = isGrouped(items) ? undefined : items;
    const isPinned = useMemo(() => {
        if (flatItems === undefined || pinnedValues.length === 0) {
            return undefined;
        }

        return (item: unknown): boolean => pinnedValues.some((held) => isSameOption(held, item));
    }, [flatItems, pinnedValues]);

    const pinnedItems = useMemo((): readonly Item[] | undefined => {
        if (flatItems === undefined || isPinned === undefined) {
            return undefined;
        }

        return [...flatItems.filter(isPinned), ...flatItems.filter((item) => !isPinned(item))];
    }, [flatItems, isPinned]);

    const context: ComboboxContextValue = {
        ...surface,
        multiple: true,
        hasValue: values.length > 0,
        isSelected: (item) => pendingValues.some((held) => isSameOption(held, item)),
        remove: (item) => {
            commit(values.filter((held) => !isSameOption(held, item)));
            setPendingValues(pendingValues.filter((held) => !isSameOption(held, item)));
        },
        confirm: () => {
            commit(pendingValues);
            setOpen(false);
        },
        isPinned,
    };

    return (
        <ComboboxPrimitive.Root
            {...rootProps}
            multiple
            items={pinnedItems ?? items}
            onOpenChange={setOpen}
            value={[...pendingValues]}
            onValueChange={setPendingValues}
        >
            <ComboboxContext.Provider value={context}>{children}</ComboboxContext.Provider>
        </ComboboxPrimitive.Root>
    );
}

type AnyComboboxProps<Item> = SingleImplProps<Item> | MultipleImplProps<Item>;

/**
 * Pick from a list too long to read, by searching it. The value row is the field and a
 * dialog — a sheet on mobile — is the surface, with the list rendered inline inside it.
 *
 * One pick commits and closes. Under `multiple` the surface holds what is ticked until a
 * `ComboboxConfirm` commits it, so it wants a `ComboboxFooter` to confirm and cancel.
 *
 * @example
 * <Combobox title="Verantwortlich" searchPlaceholder="Person suchen" items={people} value={person} onValueChange={setPerson}>
 *     <ComboboxValue placeholder="Person wählen">
 *         <ComboboxAction label="Person suchen" />
 *     </ComboboxValue>
 *     <ComboboxContent>
 *         <ComboboxBody>
 *             <ComboboxList>{(person) => <ComboboxItem key={person.value} value={person} />}</ComboboxList>
 *         </ComboboxBody>
 *     </ComboboxContent>
 * </Combobox>
 */
// Only the first signature is callable; the second is the implementation's discriminated form.
export function Combobox<Item = ComboboxOption, Multiple extends Boolish = false>(
    props: ComboboxProps<Item, Multiple>,
): ReactElement;
export function Combobox<Item = ComboboxOption>(props: AnyComboboxProps<Item>): ReactElement {
    // Narrow on `props` itself: destructuring `multiple` first leaves a union JSX cannot spread.
    return props.multiple === true ? <MultipleCombobox {...props} /> : <SingleCombobox {...props} />;
}

export type ComboboxValueProps = PickerValueProps & {
    /** Names the field while nothing is picked, shown as a dashed chip that opens it. */
    placeholder?: string;
};

/** The field: the row the picked values and the action share, or `placeholder` while empty. */
export function ComboboxValue({ placeholder, children, ...props }: ComboboxValueProps): ReactElement {
    const { hasValue } = useCombobox();

    return (
        <PickerValue data-slot="combobox-value" {...props}>
            {!hasValue && placeholder !== undefined && <ComboboxPlaceholder label={placeholder} />}
            {children}
        </PickerValue>
    );
}

export type ComboboxValueChipsProps = Omit<PickerValueChipsProps, 'onOpen'>;

/** Collapses the chips that do not fit into one that opens the surface. */
export function ComboboxValueChips({ children, ...props }: ComboboxValueChipsProps): ReactElement {
    const { setOpen } = useCombobox();

    return (
        <PickerValueChips
            data-slot="combobox-value-chips"
            onOpen={() => {
                setOpen(true);
            }}
            {...props}
        >
            {children}
        </PickerValueChips>
    );
}

export type ComboboxValueChipProps<Item> = Omit<PickerValueChipProps, 'onOpen' | 'onRemove' | 'label'> & {
    value: Item;
    /** Defaults to the option's own label. */
    label?: string;
};

export function ComboboxValueChip<Item = ComboboxOption>({
    value,
    label,
    children,
    ...props
}: ComboboxValueChipProps<Item>): ReactElement {
    const { setOpen, remove } = useCombobox();

    return (
        <PickerValueChip
            data-slot="combobox-value-chip"
            label={label ?? optionLabel(value)}
            onOpen={() => {
                setOpen(true);
            }}
            onRemove={() => {
                remove(value);
            }}
            {...props}
        >
            {children}
        </PickerValueChip>
    );
}

type ComboboxPlaceholderProps = Omit<PickerValueSuggestionProps, 'onClick'>;

function ComboboxPlaceholder({ children, ...props }: ComboboxPlaceholderProps): ReactElement {
    const { setOpen } = useCombobox();

    return (
        <PickerValueSuggestion
            data-slot="combobox-placeholder"
            onClick={() => {
                setOpen(true);
            }}
            {...props}
        >
            {children}
        </PickerValueSuggestion>
    );
}

export type ComboboxActionProps = Omit<ComponentPropsWithRef<'button'>, 'onClick'> &
    ButtonVariantProps & {
        /** Names the action for assistive technology, since no text is visible. */
        label: string;
    };

export function ComboboxAction({ children, ...props }: ComboboxActionProps): ReactElement {
    const { open, setOpen } = useCombobox();

    return (
        <PickerValueAction
            aria-haspopup="dialog"
            aria-expanded={open}
            data-slot="combobox-action"
            onClick={() => {
                setOpen(true);
            }}
            {...props}
        >
            {children ?? <SearchIcon />}
        </PickerValueAction>
    );
}

export type ComboboxContentProps = Omit<DialogContentProps, 'children'> & {
    children?: ReactNode;
};

/** The surface: a dialog on desktop, a sheet on mobile. It writes its own title and search. */
export function ComboboxContent({ children, className, ...props }: ComboboxContentProps): ReactElement {
    const { title, searchPlaceholder, open, setOpen } = useCombobox();
    const inputRef = useRef<HTMLInputElement>(null);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                variant="picker"
                snapMode="fullscreen"
                initialFocus={inputRef}
                data-slot="combobox-content"
                className={cn('sm:max-w-105', className)}
                {...props}
            >
                <DialogHeader className="flex-col items-stretch gap-default border-line-weak px-default pt-5 pb-default sm:px-default">
                    <div className="flex items-center gap-default px-cozy">
                        <DialogTitle className="text-card-title">{title}</DialogTitle>
                        <DialogIconClose data-slot="combobox-close" />
                    </div>

                    <InputGroup data-slot="combobox-search">
                        <InputGroupAddon>
                            <SearchIcon />
                        </InputGroupAddon>
                        <ComboboxPrimitive.Input
                            ref={inputRef}
                            placeholder={searchPlaceholder}
                            aria-label={title}
                            aria-expanded
                            render={<InputGroupInput />}
                        />
                    </InputGroup>
                </DialogHeader>

                {children}
            </DialogContent>
        </Dialog>
    );
}

export type ComboboxBodyProps = DialogBodyProps;

export function ComboboxBody({ children, className, ...props }: ComboboxBodyProps): ReactElement {
    return (
        <DialogBody data-slot="combobox-body" className={cn('px-0 py-cozy sm:px-0', className)} {...props}>
            {children}
        </DialogBody>
    );
}

export type ComboboxFooterProps = DialogFooterProps;

/** Where the buttons that commit or drop what the surface holds go — `multiple` needs both. */
export function ComboboxFooter({ children, className, ...props }: ComboboxFooterProps): ReactElement {
    return (
        <DialogFooter
            data-slot="combobox-footer"
            className={cn('px-default sm:px-default', className)}
            {...props}
        >
            {children}
        </DialogFooter>
    );
}

export type ComboboxCancelProps = DialogCloseProps;

/** Closes the surface, dropping what it held. */
export function ComboboxCancel({ children, ...props }: ComboboxCancelProps): ReactElement {
    return (
        <DialogClose data-slot="combobox-cancel" variant="secondary" {...props}>
            {children}
        </DialogClose>
    );
}

export type ComboboxConfirmProps = Omit<ComponentPropsWithRef<'button'>, 'onClick'> & ButtonVariantProps;

/** Commits what the surface holds. */
export function ComboboxConfirm(props: ComboboxConfirmProps): ReactElement {
    const { confirm } = useCombobox();

    return (
        <Button
            variant="primary"
            data-slot="combobox-confirm"
            onClick={() => {
                confirm();
            }}
            {...props}
        />
    );
}

export type ComboboxListProps<Item> = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    /** A template called per filtered option, or the rows written out by hand. */
    children?: ReactNode | ((item: Item, index: number) => ReactNode);
};

/** The listbox holding the rows the search left, ruled off where the pinned rows stop. */
export function ComboboxList<Item = ComboboxOption>({
    children,
    className,
    ...props
}: ComboboxListProps<Item>): ReactElement {
    const { title, isPinned } = useCombobox();
    // Only a template can be ruled off: hand-written rows are rendered as they are.
    const ruled = isPinned !== undefined && typeof children === 'function';

    return (
        <ComboboxPrimitive.List
            aria-label={title}
            data-slot="combobox-list"
            className={cn('flex w-full flex-col gap-tight px-cozy outline-none', className)}
            {...props}
        >
            {ruled ? <ComboboxRuledRows isPinned={isPinned}>{children}</ComboboxRuledRows> : children}
        </ComboboxPrimitive.List>
    );
}

type ComboboxRuledRowsProps<Item> = {
    isPinned: (item: unknown) => boolean;
    children: (item: Item, index: number) => ReactNode;
};

/**
 * Renders the caller's template with a rule where the picked rows give way to the rest.
 * The boundary is read off Base UI's filtered list, hence a component of its own.
 */
function ComboboxRuledRows<Item>({ isPinned, children }: ComboboxRuledRowsProps<Item>): ReactElement {
    const filtered = ComboboxPrimitive.useFilteredItems<Item>();
    const pinnedCount = filtered.filter((item) => isPinned(item)).length;

    return (
        <ComboboxPrimitive.Collection>
            {(item: Item, index: number) => (
                <Fragment key={index}>
                    {index === pinnedCount && pinnedCount > 0 && <ComboboxDivider />}
                    {children(item, index)}
                </Fragment>
            )}
        </ComboboxPrimitive.Collection>
    );
}

export type ComboboxGroupProps<Item> = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    /** The section's heading — its `value` from `items`. */
    label: string;
    items: readonly Item[];
    children?: ReactNode | ((item: Item, index: number) => ReactNode);
};

/** A section of the list, for the template a grouped `items` list hands its groups to. */
export function ComboboxGroup<Item = ComboboxOption>({
    label,
    items,
    children,
    className,
    ...props
}: ComboboxGroupProps<Item>): ReactElement {
    return (
        <ComboboxPrimitive.Group
            items={items}
            data-slot="combobox-group"
            className={cn('flex flex-col gap-[inherit]', className)}
            {...props}
        >
            <ComboboxPrimitive.GroupLabel className="px-cozy pt-cozy pb-tight text-ui-sm font-medium tracking-[0.06em] text-content-weaker">
                {label}
            </ComboboxPrimitive.GroupLabel>
            {typeof children === 'function' ? (
                <ComboboxPrimitive.Collection>{children}</ComboboxPrimitive.Collection>
            ) : (
                children
            )}
        </ComboboxPrimitive.Group>
    );
}

const itemRowClasses = [
    'flex w-full flex-row items-center gap-cozy',
    'min-h-9 px-cozy py-tight',
    'text-left text-ui-base text-content',
    'rounded-md cursor-pointer select-none outline-none',
    'active:translate-px transition duration-state ease-state',
];

type ComboboxDividerProps = ComponentPropsWithRef<'div'>;

function ComboboxDivider({ className, ...props }: ComboboxDividerProps): ReactElement {
    return (
        <div
            aria-hidden
            data-slot="combobox-divider"
            className={cn('my-tight h-px bg-line-weak', className)}
            {...props}
        />
    );
}

export type ComboboxItemProps<Item> = Omit<
    ComboboxPrimitive.Item.Props,
    'value' | 'render' | 'nativeButton' | 'className'
> & {
    value: Item;
    /** The row's text, and its accessible name. Defaults to the option's own label. */
    label?: string;
    className?: string;
};

/**
 * One option; without children it renders its label and indicator. A real `<button>`,
 * because a sheet's swipe-to-dismiss swallows clicks on anything else.
 */
export function ComboboxItem<Item = ComboboxOption>({
    value,
    label,
    children,
    className,
    ...props
}: ComboboxItemProps<Item>): ReactElement {
    const { isSelected } = useCombobox();
    const resolvedLabel = label ?? optionLabel(value);

    return (
        <ComboboxItemContext.Provider value={{ label: resolvedLabel, selected: isSelected(value) }}>
            <ComboboxPrimitive.Item
                value={value}
                nativeButton
                render={<button type="button" />}
                aria-label={resolvedLabel}
                data-slot="combobox-item"
                className={cn(
                    itemRowClasses,
                    'data-highlighted:bg-content/(--opacity-wash)',
                    'data-disabled:pointer-events-none data-disabled:opacity-disabled',
                    className,
                )}
                {...props}
            >
                {children ?? (
                    <>
                        <ComboboxItemLabel />
                        <ComboboxItemIndicator />
                    </>
                )}
            </ComboboxPrimitive.Item>
        </ComboboxItemContext.Provider>
    );
}

export type ComboboxItemLeadingProps = MenuItemLeadingProps;

export function ComboboxItemLeading({
    children,
    className,
    ...props
}: ComboboxItemLeadingProps): ReactElement {
    return (
        <MenuItemLeading
            data-slot="combobox-item-leading"
            className={cn("text-content-weak [&_svg:not([class*='size-'])]:size-4", className)}
            {...props}
        >
            {children}
        </MenuItemLeading>
    );
}

export type ComboboxItemLabelProps = ComponentPropsWithRef<'span'>;

export function ComboboxItemLabel({ children, className, ...props }: ComboboxItemLabelProps): ReactElement {
    const { label } = useContext(ComboboxItemContext);

    return (
        <span
            data-slot="combobox-item-label"
            className={cn('flex min-w-0 flex-1 flex-col gap-tight', className)}
            {...props}
        >
            {children ?? <span className="truncate">{label}</span>}
        </span>
    );
}

export type ComboboxItemTrailingProps = MenuItemTrailingProps;

export function ComboboxItemTrailing({ children, ...props }: ComboboxItemTrailingProps): ReactElement {
    return (
        <MenuItemTrailing data-slot="combobox-item-trailing" {...props}>
            {children}
        </MenuItemTrailing>
    );
}

export type ComboboxItemIndicatorProps = MenuItemTrailingProps;

export function ComboboxItemIndicator({
    children,
    ...props
}: ComboboxItemIndicatorProps): ReactElement | null {
    const { multiple } = useCombobox();
    const { selected } = useContext(ComboboxItemContext);

    if (children === undefined && !multiple && !selected) {
        return null;
    }

    return (
        <MenuItemTrailing data-slot="combobox-item-indicator" {...props}>
            {children ??
                (multiple ? (
                    <span
                        aria-hidden
                        data-state={selected ? 'checked' : 'unchecked'}
                        className={cn(checkboxVariants({ variant: 'primary' }), 'pointer-events-none')}
                    />
                ) : (
                    <CheckIcon className="size-4 text-brand" strokeWidth={2.5} />
                ))}
        </MenuItemTrailing>
    );
}

export type ComboboxCreateProps = Omit<ComponentPropsWithRef<'button'>, 'onClick'> & {
    /** Names the row, e.g. `„{query}“ einladen`. */
    label: (query: string) => string;
    /** Makes the option the query stands for. The caller commits the value. */
    onCreate: (query: string) => void;
};

/**
 * Offers to create what was typed. An ordinary button reached with Tab, sitting after the
 * list: a listbox may only own options, and Base UI indexes the arrow keys from `items`.
 */
export function ComboboxCreate({
    label,
    onCreate,
    children,
    className,
    ...props
}: ComboboxCreateProps): ReactElement | null {
    const { multiple, query, setOpen } = useCombobox();
    const trimmedQuery = query.trim();

    if (trimmedQuery === '') {
        return null;
    }

    const resolvedLabel = label(trimmedQuery);

    return (
        <ComboboxItemContext.Provider value={{ label: resolvedLabel, selected: false }}>
            <button
                type="button"
                aria-label={resolvedLabel}
                data-slot="combobox-create"
                className={cn(itemRowClasses, 'hover:bg-content/(--opacity-wash)', className)}
                onClick={() => {
                    onCreate(trimmedQuery);

                    // Only the caller knows what it created, so `multiple` keeps what it holds
                    // and lets the new row be ticked with the rest.
                    if (!multiple) {
                        setOpen(false);
                    }
                }}
                {...props}
            >
                {children ?? (
                    <>
                        <ComboboxItemLabel />
                        <ComboboxItemTrailing>
                            <PlusIcon className="size-4" />
                        </ComboboxItemTrailing>
                    </>
                )}
            </button>
        </ComboboxItemContext.Provider>
    );
}

export type ComboboxEmptyProps = EmptyStateProps;

/** Shown instead of the list when the search comes up empty. Needs `items` on the root. */
export function ComboboxEmpty({ children, ...props }: ComboboxEmptyProps): ReactElement {
    return (
        <ComboboxPrimitive.Empty data-slot="combobox-empty">
            <EmptyState {...props}>{children}</EmptyState>
        </ComboboxPrimitive.Empty>
    );
}
